import { inject, injectable } from 'inversify'
import { computed, makeObservable, reaction } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { DocumentGateway } from '@core/services/document.gateway'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { RouterRepository } from '@core/routing/router.repository'
import { PageTitleConfig } from './page-title.config'

/**
 * Keeps `document.title` in step with the committed route.
 *
 * A Presenter rather than something inside Router, because a title is *copy
 * about the current route*, which is a presentation concern — Router would
 * otherwise have to know about i18n and about how a title is composed. It also
 * means the title follows a locale switch for free: `title` is a computed over
 * both the route and the locale, so the reaction fires on either.
 *
 * Registered as an AppLifecycle participant (§R), so adding it cost one line in
 * the composition root and no edit to AppPresenter.
 */
@injectable()
export class PageTitlePresenter implements AppLifecycle {
  @inject(RouterRepository)
  private routerRepository!: RouterRepository

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  @inject(PageTitleConfig)
  private pageTitleConfig!: PageTitleConfig

  @inject(Types.IDocumentGateway)
  private documentGateway!: DocumentGateway

  private watchesTitle = false

  constructor() {
    makeObservable(this, { title: computed })
  }

  get title(): string {
    const { routeDef } = this.routerRepository.currentRoute
    const key = routeDef?.titleKey ?? this.pageTitleConfig.fallbackTitleKey

    return this.pageTitleConfig.compose(this.i18nRepository.t(key))
  }

  /**
   * The document's title is an application-lifetime effect: it ends with the
   * document, so there is no narrower owner to hand a disposer to. Registration
   * is guarded instead of disposable, which is what AppLifecycle's
   * "safe to call twice" contract actually needs.
   */
  initialize(): void {
    if (this.watchesTitle) return

    // fireImmediately: the first route commits during bootstrap, which can be
    // before or after this runs depending on registration order — and §R only
    // promises participants are order-independent, so it must not matter.
    reaction(
      () => this.title,
      (title) => this.documentGateway.setTitle(title),
      { fireImmediately: true },
    )
    this.watchesTitle = true
  }
}
